export interface FeatureFlags {
  peerReviewsEnabled: boolean;
  paperclipPricing: boolean;
  billing: boolean;
  /**
   * Citation graph explorer — the "Citations" op inside the Playground.
   * Gated separately from the playground itself so the explorer can stay
   * hidden on prod. Citation *counts* in search results are backend
   * enrichment and ignore this flag entirely.
   */
  citegraphExplorer: boolean;
  /** Two-ink dithered library illustration above the home masthead. */
  homeHeroArt: boolean;
  /** Dataset builder page + nav entry, and its API on the backend. On in local
   * dev; in staging and prod, only where ConfigCat enables it for the user. */
  dataCuration: boolean;
  /** Evidence API page (/evidence-api). Always on outside prod; on prod only
   * where enabled in ConfigCat (the backend gates /api/v1/claims/* the same way). */
  evidenceApi: boolean;
  /** Dataset builder modes fast, accuracy (shown as Comprehensive), and advanced.
   * The backend checks the same switch for each map. */
  paperclipMapModels: boolean;
  /** Datasets from the dataset builder service. /api/features/ sends the
   * backend's own decision for the user: the ConfigCat flag, or an email domain
   * in DATASET_BUILDER_FAST_DOMAINS (staging: gxl.ai). */
  datasetBuilder: boolean;
  /** Workflows page + nav entry, and its API on the backend. The ConfigCat key
   * is still "routines". On in local dev; in staging and prod, only where
   * ConfigCat enables it for the user. */
  routines: boolean;
  /** Usage tracking page + nav entry: the cost and the time of the runs of
   * all users. The ConfigCat key is snake_case. On in local dev; in staging and
   * prod, only where ConfigCat enables it, and the backend also requires a
   * gxl.ai email. */
  data_usage_tracking: boolean;
  /** Repositories nav entry and sidebar list, next to the Dataset builder. The
   * ConfigCat key is snake_case. Without it, repository pages open under
   * Clipboard. */
  repo_visualization: boolean;
}

// Never set these to true. If you want to turn on a feature, toggle it in the ConfigCat dashboard.
// https://app.configcat.com/
// Add new paperclip frontend flags to this type and DEFAULT_FEATURE_FLAGS.
export const DEFAULT_FEATURE_FLAGS: FeatureFlags = {
  peerReviewsEnabled: false,
  paperclipPricing: false,
  billing: false,
  citegraphExplorer: false,
  homeHeroArt: false,
  dataCuration: false,
  evidenceApi: false,
  paperclipMapModels: false,
  datasetBuilder: false,
  routines: false,
  data_usage_tracking: false,
  repo_visualization: false,
};

/** Repositories get their own nav entry only with both flags. */
export const showRepositories = (flags: FeatureFlags) =>
  flags.dataCuration && flags.repo_visualization;

export const FEATURE_FLAG_KEYS = Object.keys(DEFAULT_FEATURE_FLAGS) as (keyof FeatureFlags)[];

export const pickFeatureFlags = (data: Record<string, unknown>): FeatureFlags => {
  const flags = { ...DEFAULT_FEATURE_FLAGS };
  for (const key of FEATURE_FLAG_KEYS) {
    const value = data[key];
    if (value !== undefined) {
      flags[key] = Boolean(value);
    }
  }
  return flags;
};

/** Flat values for React memo deps — order matches FEATURE_FLAG_KEYS. */
export const featureFlagMemoDeps = (
  ...sources: Array<FeatureFlags | Partial<FeatureFlags>>
): Array<boolean | undefined> =>
  FEATURE_FLAG_KEYS.flatMap((key) => sources.map((source) => source[key]));
